/**
 * Logout should silence this phone only.
 * Match the push token, then this install's device id.
 * A platform or an empty request matches nothing: turning off every iOS
 * row also silences the other phone. The miss is logged and no row changes.
 * Registration writes device_id onto the existing token row so an older
 * row is matched the next time this install registers.
 * No object spread: this file is required from both the app and functions.
 */

const PLATFORMS = { ios: true, android: true, web: true };

function cleanToken(token) {
  return typeof token === 'string' ? token.trim() : '';
}

function cleanPlatform(platform) {
  const name = typeof platform === 'string' ? platform.trim().toLowerCase() : '';
  return PLATFORMS[name] ? name : '';
}

function cleanDeviceId(deviceId) {
  const value = typeof deviceId === 'string' ? deviceId.trim() : '';
  if (!/^[A-Za-z0-9-]{8,80}$/.test(value)) return '';
  return value;
}

function deviceColumnMissing(error) {
  if (!error) return false;
  return /device_id/i.test(String(error.message || ''));
}

/**
 * Attempts are tried in order. The first one that updates a row wins.
 * Platform is ignored so one logout cannot turn off every phone.
 */
function deviceTokenDeactivate(userId, token, platform, deviceId) {
  const id = typeof userId === 'string' ? userId.trim() : '';
  if (!id) return { error: 'user is required' };
  const attempts = [];
  const value = cleanToken(token);
  const install = cleanDeviceId(deviceId);
  if (value) attempts.push({ mode: 'token', token: value });
  if (install) attempts.push({ mode: 'device', deviceId: install });
  return { userId: id, attempts, platform: cleanPlatform(platform) };
}

/** Fields written on registration. device_id fills in on the existing token row. */
function cleanApnsEnvironment(value) {
  const name = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (name === 'sandbox' || name === 'production') return name;
  return '';
}

function deviceTokenRegistration(userId, record) {
  const source = record && typeof record === 'object' ? record : {};
  const id = typeof userId === 'string' ? userId.trim() : '';
  const token = cleanToken(source.token);
  const platform = cleanPlatform(source.platform);
  if (!id || !token || !platform) return { error: 'token and platform are required' };
  const row = {
    user_id: id,
    token,
    platform,
    is_active: source.is_active !== false,
  };
  const deviceId = cleanDeviceId(source.deviceId || source.device_id);
  if (deviceId) row.device_id = deviceId;
  const environment = cleanApnsEnvironment(source.apnsEnvironment || source.apns_environment);
  if (environment) row.apns_environment = environment;
  return { row };
}

function applyDeviceAttempt(supabase, userId, attempt) {
  let query = supabase
    .from('device_tokens')
    .update({ is_active: false })
    .select('id')
    .eq('user_id', userId);
  if (attempt.mode === 'token') query = query.eq('token', attempt.token);
  else query = query.eq('device_id', attempt.deviceId);
  return query;
}

async function deactivateDeviceTokens(supabase, userId, record) {
  const source = record && typeof record === 'object' ? record : {};
  const target = deviceTokenDeactivate(
    userId,
    source.token,
    source.platform,
    source.deviceId || source.device_id,
  );
  if (target.error) return target;
  for (let index = 0; index < target.attempts.length; index += 1) {
    const attempt = target.attempts[index];
    let result;
    try {
      result = await applyDeviceAttempt(supabase, target.userId, attempt);
    } catch (error) {
      if (attempt.mode === 'device' && deviceColumnMissing(error)) continue;
      throw error;
    }
    if (result && result.error) {
      if (attempt.mode === 'device' && deviceColumnMissing(result.error)) continue;
      throw result.error;
    }
    const rows = result && Array.isArray(result.data) ? result.data : [];
    if (rows.length > 0) return { ok: true, mode: attempt.mode, count: rows.length };
  }
  console.error('Push logout deactivated nothing', target.userId);
  return { ok: true, mode: 'none', count: 0 };
}

function apnsColumnMissing(error) {
  if (!error) return false;
  return /apns_environment/i.test(String(error.message || ''));
}

async function upsertDeviceToken(supabase, row) {
  const attempt = {};
  Object.keys(row || {}).forEach((key) => {
    attempt[key] = row[key];
  });
  let saved = await supabase.from('device_tokens').upsert(attempt, { onConflict: 'user_id,token,platform' });
  if (saved && saved.error && attempt.device_id && deviceColumnMissing(saved.error)) {
    delete attempt.device_id;
    saved = await supabase.from('device_tokens').upsert(attempt, { onConflict: 'user_id,token,platform' });
  }
  if (saved && saved.error && attempt.apns_environment && apnsColumnMissing(saved.error)) {
    delete attempt.apns_environment;
    saved = await supabase.from('device_tokens').upsert(attempt, { onConflict: 'user_id,token,platform' });
  }
  return saved;
}

module.exports = {
  cleanDeviceId,
  cleanApnsEnvironment,
  deviceColumnMissing,
  apnsColumnMissing,
  deviceTokenDeactivate,
  deviceTokenRegistration,
  deactivateDeviceTokens,
  upsertDeviceToken,
};
